// Pure transition functions for equipment / inventory mutation. Step 9 (1/4).
//
// Exported as pure functions so unit tests and future flows (drop pickup
// in Step 10+, craft in Step 11+) can reuse them without going through
// the store. No I/O, no time reads, no mutation of the input.
//
// Invariants:
// - Each slot holds at most one equipped Item. `applyEquip` enforces this
//   by stripping the existing occupant before pushing the new one.
// - Equip is **swap**: when a slot is already occupied, the outgoing Item
//   goes back to the inventory in the same step (zero-loss).
// - `applyEquip` requires the item to currently live in the inventory.
//   Equipping something the character does not own is a programming error
//   and throws.
// - Two-hand weapons (Step 9 follow-up) occupy weaponMain and forbid
//   anything in offHand. applyEquip displaces offHand when a 2H lands in
//   weaponMain and vice versa. The caller injects the predicate via
//   `isTwoHanded` so this module stays catalogue-free.

import type { Item, ItemId, Slot } from '@renavora/shared';

/** Catalogue-resolution predicate injected by the store layer. */
export type IsTwoHanded = (item: Item) => boolean;

export interface InventoryState {
  equipment: ReadonlyArray<Item>;
  inventory: ReadonlyArray<Item>;
}

export function applyEquip(
  state: InventoryState,
  item: Item,
  isTwoHanded: IsTwoHanded,
): InventoryState {
  const inInventory = state.inventory.some((i) => i.id === item.id);
  if (!inInventory) {
    throw new Error(`equipItem: ${item.id} not in inventory`);
  }

  // Build the set of slots the incoming item will displace. The target
  // slot is always displaced. Two-hand weapons also displace offHand,
  // and equipping into offHand displaces a 2H weapon currently in
  // weaponMain. weaponMain absent + equipping offHand is fine (no
  // 2H to displace).
  const displacedSlots = new Set<Slot>([item.slot]);
  if (item.slot === 'weaponMain' && isTwoHanded(item)) {
    displacedSlots.add('offHand');
  } else if (item.slot === 'offHand') {
    const currentMain = state.equipment.find((e) => e.slot === 'weaponMain');
    if (currentMain !== undefined && isTwoHanded(currentMain)) {
      displacedSlots.add('weaponMain');
    }
  }

  const displaced = state.equipment.filter((e) => displacedSlots.has(e.slot));
  return {
    equipment: [
      ...state.equipment.filter((e) => !displacedSlots.has(e.slot)),
      item,
    ],
    inventory: [
      ...state.inventory.filter((i) => i.id !== item.id),
      ...displaced,
    ],
  };
}

export function applyUnequip(state: InventoryState, slot: Slot): InventoryState {
  const existing = state.equipment.find((e) => e.slot === slot);
  if (existing === undefined) {
    // Empty slot — no-op. Return the same reference so callers can detect.
    return state;
  }
  return {
    equipment: state.equipment.filter((e) => e.slot !== slot),
    inventory: [...state.inventory, existing],
  };
}

export function applyDiscard(state: InventoryState, itemId: ItemId): InventoryState {
  const exists = state.inventory.some((i) => i.id === itemId);
  if (!exists) return state;
  return {
    equipment: state.equipment,
    inventory: state.inventory.filter((i) => i.id !== itemId),
  };
}
